import { useCallback, useEffect, useRef, useState } from 'react';

// ─── useAutosave ────────────────────────────────────────────────────────────
// Debounced save trigger. Call schedule(payload) on every change; the latest
// payload is written via `save` after `delayMs` of quiet. The pending timer is
// always cancelled on unmount or when schedule is called again.
export type SaveStatus = 'idle' | 'saving' | 'saved';

export function useAutosave<T>(
  save: (payload: T) => unknown,
  delayMs = 1200,
  savedFlashMs = 1200,
): { schedule: (payload: T) => void; status: SaveStatus } {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flash = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveRef = useRef(save);
  const [status, setStatus] = useState<SaveStatus>('idle');

  useEffect(() => { saveRef.current = save; }, [save]);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
    if (flash.current) clearTimeout(flash.current);
  }, []);

  const schedule = useCallback((payload: T): void => {
    setStatus('saving');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        await saveRef.current(payload);
        setStatus('saved');
        if (flash.current) clearTimeout(flash.current);
        flash.current = setTimeout(() => setStatus('idle'), savedFlashMs);
      } catch {
        setStatus('idle');
      }
    }, delayMs);
  }, [delayMs, savedFlashMs]);

  return { schedule, status };
}

// ─── useClickOutside ────────────────────────────────────────────────────────
// Calls `handler` when a mousedown happens outside the referenced node. Only
// active while `enabled` is true so menu/popover hosts don't pay listener cost
// when closed.
export function useClickOutside<T extends HTMLElement>(
  ref: React.RefObject<T | null>,
  handler: () => void,
  enabled: boolean,
): void {
  useEffect(() => {
    if (!enabled) return;
    const onMouseDown = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) handler();
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [enabled, ref, handler]);
}

// ─── useHistory ─────────────────────────────────────────────────────────────
// Snapshot-based undo/redo on top of a useState setter. `push(prev)` should be
// called with the value about to be replaced (i.e. before each mutation);
// undo/redo restore via `setState`. Snapshots are JSON-serialised so the
// stored value is decoupled from the live reference.
export interface History<T> {
  push: (snapshot: T) => void;
  undo: () => void;
  redo: () => void;
  reset: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

export function useHistory<T>(
  setState: React.Dispatch<React.SetStateAction<T>>,
  options: { max?: number; onRestore?: (next: T) => void } = {},
): History<T> {
  const { max = 100, onRestore } = options;
  const past = useRef<string[]>([]);
  const future = useRef<string[]>([]);
  const [, force] = useState(0);
  const bump = useCallback(() => force(n => n + 1), []);

  const push = useCallback((snapshot: T): void => {
    past.current.push(JSON.stringify(snapshot));
    if (past.current.length > max) past.current.shift();
    future.current = [];
    bump();
  }, [max, bump]);

  const undo = useCallback((): void => {
    if (!past.current.length) return;
    setState(prev => {
      const snap = past.current.pop();
      if (snap === undefined) return prev;
      future.current.push(JSON.stringify(prev));
      const next = JSON.parse(snap) as T;
      onRestore?.(next);
      bump();
      return next;
    });
  }, [setState, onRestore, bump]);

  const redo = useCallback((): void => {
    if (!future.current.length) return;
    setState(prev => {
      const snap = future.current.pop();
      if (snap === undefined) return prev;
      past.current.push(JSON.stringify(prev));
      const next = JSON.parse(snap) as T;
      onRestore?.(next);
      bump();
      return next;
    });
  }, [setState, onRestore, bump]);

  const reset = useCallback((): void => {
    past.current = [];
    future.current = [];
    bump();
  }, [bump]);

  return {
    push, undo, redo, reset,
    canUndo: past.current.length > 0,
    canRedo: future.current.length > 0,
  };
}

// ─── useFileUpload ──────────────────────────────────────────────────────────
// Hides the FileReader boilerplate behind a single hook. Wire `inputRef` and
// `onChange` on a hidden <input type="file"/>, and call `pick()` to open the
// native file dialog. The selected file is read as a data URL and forwarded to
// `onLoad`.
export function useFileUpload(
  onLoad: (dataUrl: string, file: File) => void,
): {
  inputRef: React.RefObject<HTMLInputElement>;
  pick: () => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
} {
  const inputRef = useRef<HTMLInputElement>(null);
  const onLoadRef = useRef(onLoad);
  useEffect(() => { onLoadRef.current = onLoad; }, [onLoad]);

  const pick = useCallback((): void => inputRef.current?.click(), []);

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const src = reader.result;
      if (typeof src === 'string') onLoadRef.current(src, file);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }, []);

  return { inputRef, pick, onChange };
}
